import React, { useMemo, useState } from 'react';
import { alpha, Box, Button, Popover, Stack, Typography } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';
import { getDatePickerProviderProps } from '../../../../utils/datePickerLocale';
import { RECON_UI } from '../../utils/reconConstants';
import {
  PERIOD_PRESET_ORDER,
  PERIOD_PRESETS,
  getPeriodRange,
  isCustomRangeWithinLimit,
} from '../../utils/periodPresets';

const toDayjs = (value) => (value ? dayjs(value) : null);

const datesMatchPreset = (preset, startDate, endDate) => {
  if (!preset || preset === PERIOD_PRESETS.CUSTOM || !startDate || !endDate) {
    return false;
  }
  const resolved = getPeriodRange(preset);
  if (!resolved) {
    return false;
  }
  return (
    resolved.start.format('YYYY-MM-DD') === dayjs(startDate).format('YYYY-MM-DD') &&
    resolved.end.format('YYYY-MM-DD') === dayjs(endDate).format('YYYY-MM-DD')
  );
};

const detectPreset = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return '';
  }
  const start = dayjs(startDate).format('YYYY-MM-DD');
  const end = dayjs(endDate).format('YYYY-MM-DD');
  for (const key of PERIOD_PRESET_ORDER.filter((item) => item !== PERIOD_PRESETS.CUSTOM)) {
    const resolved = getPeriodRange(key);
    if (resolved && resolved.start.format('YYYY-MM-DD') === start && resolved.end.format('YYYY-MM-DD') === end) {
      return key;
    }
  }
  return PERIOD_PRESETS.CUSTOM;
};

const resolveActivePreset = (periodPreset, startDate, endDate) => {
  if (periodPreset && periodPreset !== PERIOD_PRESETS.CUSTOM && datesMatchPreset(periodPreset, startDate, endDate)) {
    return periodPreset;
  }
  if (periodPreset === PERIOD_PRESETS.CUSTOM) {
    return PERIOD_PRESETS.CUSTOM;
  }
  return detectPreset(startDate, endDate);
};

const ReconPeriodFilter = ({ periodPreset, startDate, endDate, onChange, disabled = false }) => {
  const { reconciliation: r, language } = useTranslatedMessages();
  const ft = r.filters ?? {};
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftPreset, setDraftPreset] = useState('');
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [rangeError, setRangeError] = useState(false);
  const open = Boolean(anchorEl);

  const activePreset = useMemo(
    () => resolveActivePreset(periodPreset, startDate, endDate),
    [periodPreset, startDate, endDate],
  );

  const emitChange = (nextStart, nextEnd, nextPreset) => {
    onChange({
      startDate: nextStart ? dayjs(nextStart).format('YYYY-MM-DD') : null,
      endDate: nextEnd ? dayjs(nextEnd).format('YYYY-MM-DD') : null,
      periodPreset: nextPreset,
    });
  };

  const openPopover = (event) => {
    setDraftPreset(activePreset || '');
    setDraftStart(toDayjs(startDate));
    setDraftEnd(toDayjs(endDate));
    setRangeError(false);
    setAnchorEl(event.currentTarget);
  };

  const applyPreset = (preset) => {
    setDraftPreset(preset);
    setRangeError(false);
    if (preset === PERIOD_PRESETS.CUSTOM) {
      return;
    }
    const resolved = getPeriodRange(preset);
    if (!resolved) {
      return;
    }
    emitChange(resolved.start, resolved.end, preset);
    setAnchorEl(null);
  };

  const applyCustom = () => {
    if (!draftStart || !draftEnd) {
      return;
    }
    if (!isCustomRangeWithinLimit(draftStart, draftEnd)) {
      setRangeError(true);
      return;
    }
    emitChange(draftStart, draftEnd, PERIOD_PRESETS.CUSTOM);
    setAnchorEl(null);
  };

  const clearCustom = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setRangeError(false);
  };

  const presetLabel = (key) => ft.presets?.[key] ?? key;

  const buttonLabel = (() => {
    if (activePreset && activePreset !== PERIOD_PRESETS.CUSTOM) {
      return presetLabel(activePreset);
    }
    if (startDate && endDate) {
      return `${dayjs(startDate).format('DD/MM/YY')} - ${dayjs(endDate).format('DD/MM/YY')}`;
    }
    if (activePreset === PERIOD_PRESETS.CUSTOM) {
      return ft.custom_range ?? 'Custom Range';
    }
    return ft.period ?? ft.select_period ?? 'Select period';
  })();

  const rangeSummary = (() => {
    if (draftStart && draftEnd) {
      return `${draftStart.format('MMM D')} - ${draftEnd.format('MMM D')}`;
    }
    if (draftStart) {
      return draftStart.format('MMM D');
    }
    return ft.select_dates ?? 'Select dates';
  })();

  return (
    <>
      <Button
        type="button"
        size="small"
        variant="outlined"
        disabled={disabled}
        onClick={openPopover}
        startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
        sx={{
          textTransform: 'none',
          height: 34,
          px: 1.5,
          minWidth: 200,
          flexShrink: 0,
          justifyContent: 'space-between',
          fontSize: 13,
          fontWeight: 400,
          borderColor: 'divider',
          bgcolor: RECON_UI.SURFACE_WHITE,
          color: RECON_UI.TAB_TEXT_SELECTED,
          '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: 'inherit' },
        }}
      >
        {buttonLabel}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, overflow: 'visible' } } }}
      >
        <Stack direction="row">
          <Box sx={{ p: 0.5, minWidth: 160 }}>
            {PERIOD_PRESET_ORDER.map((key) => (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => applyPreset(key)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'block',
                  width: '100%',
                  boxSizing: 'border-box',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  fontSize: 14,
                  fontWeight: draftPreset === key ? 600 : 400,
                  color: draftPreset === key ? 'primary.main' : 'text.primary',
                  bgcolor: draftPreset === key ? alpha('#4f5efa', 0.1) : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {presetLabel(key)}
              </Box>
            ))}
          </Box>

          {draftPreset === PERIOD_PRESETS.CUSTOM && (
            <Box sx={{ p: 1.5, borderLeft: 1, borderColor: 'divider', minWidth: 280 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  mb: 1.5,
                  borderRadius: 1,
                  border: 1,
                  borderColor: draftStart ? alpha('#4f5efa', 0.3) : 'divider',
                  bgcolor: draftStart ? alpha('#4f5efa', 0.05) : 'action.hover',
                  textAlign: 'center',
                  fontSize: 14,
                  color: draftStart ? 'text.primary' : 'text.secondary',
                }}
              >
                {rangeSummary}
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs} {...getDatePickerProviderProps(language)}>
                <DateCalendar
                  value={draftEnd || draftStart}
                  onChange={(date) => {
                    setRangeError(false);
                    if (!draftStart || (draftStart && draftEnd)) {
                      setDraftStart(date);
                      setDraftEnd(null);
                      return;
                    }
                    if (date.isBefore(draftStart, 'day')) {
                      setDraftStart(date);
                      setDraftEnd(null);
                      return;
                    }
                    setDraftEnd(date);
                  }}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>

              {rangeError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  {ft.custom_max ?? 'Custom range cannot exceed 6 months'}
                </Typography>
              )}

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={clearCustom}
                  sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
                >
                  {ft.clear ?? 'Clear'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!draftStart || !draftEnd}
                  onClick={applyCustom}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  {ft.apply ?? 'Apply'}
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Popover>
    </>
  );
};

export default ReconPeriodFilter;
